/**
 * 订单详情抽屉 - 人员调用核算区块
 *
 * 仅用户视角且收入订单存在衍生支出时显示：展示订单收入、人员支出、
 * 预计利润与衍生支出订单列表（可跳转到对应支出订单详情）。
 * 受控组件，不持有业务状态。
 */
import type { OrderItem } from '../../../../../types';

interface Props {
  derivedOrders: NonNullable<OrderItem['derivedExpenseOrders']>;
  totalRelatedExpense: number;
  orderAmount: number | null;
  onNavigateToOrder?: (orderId: string) => void;
}

export default function DerivedExpenseSection({ derivedOrders, totalRelatedExpense, orderAmount, onNavigateToOrder }: Props) {
  const income = Number(orderAmount ?? 0);
  const expense = Number(totalRelatedExpense ?? 0);
  const profit = income - expense;
  const profitPositive = profit >= 0;
  return (
    <>
      {/* 人员调用支出核算（仅用户可见且仅收入订单存在衍生支出时显示） */}
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">人员调用核算</p>
        <div className="bg-gradient-to-br from-indigo-50 to-white border border-indigo-100 rounded-xl p-4">
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <p className="text-[11px] text-gray-400 mb-1">订单收入</p>
              <p className="text-sm font-bold text-emerald-600">¥{income.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="text-center border-x border-indigo-100">
              <p className="text-[11px] text-gray-400 mb-1">人员支出</p>
              <p className="text-sm font-bold text-rose-500">-¥{expense.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</p>
            </div>
            <div className="text-center">
              <p className="text-[11px] text-gray-400 mb-1">预计利润</p>
              <p className={`text-sm font-bold ${profitPositive ? 'text-teal-600' : 'text-rose-600'}`}>¥{profit.toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-indigo-100 space-y-1.5">
            <p className="text-[11px] text-gray-400 flex items-center gap-1"><i className="ri-list-check-2 text-xs" />共 {derivedOrders.length} 条衍生支出订单</p>
            {derivedOrders.map(d => (
              <button
                key={d.id}
                type="button"
                onClick={() => { if (onNavigateToOrder) { onNavigateToOrder(d.id); } }}
                className="w-full flex items-center justify-between gap-2 px-2.5 py-1.5 bg-white/80 hover:bg-white border border-indigo-100 rounded-lg cursor-pointer transition-colors text-left"
                title={onNavigateToOrder ? '跳转到支出订单详情' : '衍生支出订单'}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-[10px] font-mono text-gray-400 truncate">{d.orderNumber ?? d.id.slice(0, 8).toUpperCase()}</span>
                  <span className="text-xs text-gray-700 truncate">{d.fullName ?? '-'}</span>
                  {d.role && <span className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full shrink-0">{d.role}</span>}
                  {d.approvalStatus && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 ${d.approvalStatus === '待审核' ? 'bg-amber-50 text-amber-600' : d.approvalStatus === '已通过' ? 'bg-teal-50 text-teal-600' : 'bg-rose-50 text-rose-500'}`}>{d.approvalStatus}</span>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <span className="text-xs font-semibold text-rose-500">¥{Number(d.amount ?? 0).toLocaleString('zh-CN', { minimumFractionDigits: 2 })}</span>
                  <i className="ri-arrow-right-s-line text-gray-300 text-xs" />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}